import { missingReservationRateText } from '../utils/reservationMoney';
import React, { useState, useEffect, useContext, useMemo } from 'react';
// useLocation removed - not used
import { UserContext } from '../contexts/UserContext';
// getThemeForPage removed - not used
import API_CONFIG from '../utils/apiConfig';
import { apiCallJson } from '../utils/auth';
import styles from './ArtistReservationManager.module.css';
import ScrollPicker from './ScrollPicker/ScrollPicker';

// 다국어 텍스트
const TEXT = {
  title: {
    ko: '통계',
    en: 'Statistics',
    fr: 'Statistiques'
  },
  loading: {
    ko: '통계 데이터를 불러오는 중...',
    en: 'Loading statistics...',
    fr: 'Chargement des statistiques...'
  },
  error: {
    ko: '데이터를 불러오는데 실패했습니다.',
    en: 'Failed to load data.',
    fr: 'Échec du chargement des données.'
  },
  summary: {
    totalReservations: { ko: '총 예약', en: 'Total', fr: 'Total' },
    totalRevenue: { ko: '최소 예상매출', en: 'Minimum Forecast', fr: 'Prévision minimale' },
    depositRate: { ko: '선금률', en: 'Deposit', fr: 'Acompte' },
    avgPrice: { ko: '평균 최소 예상가', en: 'Average Minimum', fr: 'Moyenne minimale' }
  },
  types: {
    flash: { ko: '플래시', en: 'Flash', fr: 'Flash' },
    personal_project: { ko: '개인', en: 'Personal', fr: 'Personnel' },
    other: { ko: '기타', en: 'Other', fr: 'Autre' }
  },
  priceRanges: {
    under_100: { ko: '<100', en: '<100', fr: '<100' },
    '100_to_200': { ko: '100–<200', en: '100–<200', fr: '100–<200' },
    '200_to_300': { ko: '200–<300', en: '200–<300', fr: '200–<300' },
    '300_to_400': { ko: '300–<400', en: '300–<400', fr: '300–<400' },
    over_400: { ko: '≥400', en: '≥400', fr: '≥400' }
  },
  sections: {
    reservationTypes: { ko: '타입별', en: 'By Type', fr: 'Par type' },
    priceRanges: { ko: '가격대별', en: 'By Price', fr: 'Par prix' },
    monthlyTrend: { ko: '월별 추이', en: 'Monthly', fr: 'Mensuel' }
  },
  dateFormat: { ko: 'ko-KR', en: 'en-US', fr: 'fr-FR' }
};

// 금액 포맷
const formatRevenue = (amount) => {
  if (!amount || amount === 0) return '-';
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M€`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}k€`;
  return `${amount}€`;
};

// 바 차트 컴포넌트
const BarChart = ({ data, height = 100, color = '#333', textColor = '#666', highlightIndex = -1 }) => {
  const maxValue = Math.max(...data.map(item => item.value)) || 1;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'end',
      gap: '4px',
      height: `${height}px`,
      padding: '8px 0',
      width: '100%'
    }}>
      {data.map((item, index) => (
        <div key={index} style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: 1,
          height: '100%',
          justifyContent: 'flex-end'
        }}>
          <div style={{
            width: '100%',
            height: `${(item.value / maxValue) * (height - 24)}px`,
            backgroundColor: index === highlightIndex ? '#333' : (item.value > 0 ? color : '#eee'),
            borderRadius: '3px 3px 0 0',
            minHeight: '2px',
            opacity: index === highlightIndex ? 1 : 0.7,
            transition: 'all 0.3s ease'
          }} />
          <div style={{
            fontSize: '8px',
            color: index === highlightIndex ? '#333' : textColor,
            marginTop: '3px',
            textAlign: 'center',
            fontWeight: index === highlightIndex ? '600' : '400'
          }}>
            {item.label}
          </div>
        </div>
      ))}
    </div>
  );
};

// 파이 차트 컴포넌트
const PieChart = ({ data, size = 50 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        {data.map((item, index) => {
          const percentage = total === 0 ? 0 : item.value / total;
          const angle = percentage * 360;
          const r = size / 2;
          const x1 = r + r * Math.cos(Math.PI * currentAngle / 180);
          const y1 = r + r * Math.sin(Math.PI * currentAngle / 180);
          const x2 = r + r * Math.cos(Math.PI * (currentAngle + angle) / 180);
          const y2 = r + r * Math.sin(Math.PI * (currentAngle + angle) / 180);
          const largeArcFlag = angle > 180 ? 1 : 0;
          const pathData = total === 0
            ? `M ${r} ${r} m -${r}, 0 a ${r},${r} 0 1,0 ${size},0 a ${r},${r} 0 1,0 -${size},0`
            : `M ${r} ${r} L ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
          currentAngle += angle;
          return <path key={index} d={pathData} fill={item.color || '#eee'} stroke="#fff" strokeWidth="1" />;
        })}
      </svg>
    </div>
  );
};

// 비교 화살표
const ComparisonArrow = ({ value }) => {
  if (value === null || value === undefined || value === 0) return null;
  const isPositive = value > 0;
  return (
    <span style={{
      color: isPositive ? '#22c55e' : '#ef4444',
      fontSize: '9px',
      fontWeight: '500',
      marginLeft: '3px'
    }}>
      {isPositive ? '↑' : '↓'}{Math.abs(value).toFixed(0)}%
    </span>
  );
};

function ArtistReservationManager({ notifications, userInfo }) {
  const { userInfo: contextUserInfo } = useContext(UserContext);
  // location 미사용 - theme 제거됨
  // const location = useLocation();
  const lang = contextUserInfo?.preferred_language || 'ko';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);

  // 년/월 선택 상태
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);

  // 테마 설정 - 현재 사용되지 않음
  // const currentTheme = getThemeForPage(location.pathname);

  // 월별 통계 가져오기 (새 API)
  const fetchStatistics = async (year, month) => {
    setLoading(true);
    setError(null);

    try {
      const url = `${API_CONFIG.ENDPOINTS.STATISTICS.MANUAL_RESERVATIONS_OVERVIEW}?year=${year}&month=${month}`;
      const data = await apiCallJson(url);

      if (!data) {
        throw new Error('Failed to fetch statistics');
      }

      setStatistics(data);
    } catch (err) {
      console.error('통계 데이터 가져오기 실패:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 월별 요약 가져오기
  const fetchMonthlySummary = async (year) => {
    try {
      const url = `${API_CONFIG.ENDPOINTS.STATISTICS.MANUAL_RESERVATIONS_MONTHLY_SUMMARY}?year=${year}`;
      const data = await apiCallJson(url);
      if (data) {
        setMonthlyData(data.monthly_data || []);
      }
    } catch (err) {
      console.error('월별 요약 가져오기 실패:', err);
    }
  };

  // 연도별 요약 가져오기
  const fetchYearlySummary = async () => {
    try {
      const url = API_CONFIG.ENDPOINTS.STATISTICS.MANUAL_RESERVATIONS_YEARLY_SUMMARY;
      const data = await apiCallJson(url);
      if (data) {
        setYearlyData(data.yearly_data || []);
      }
    } catch (err) {
      console.error('연도별 요약 가져오기 실패:', err);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    if (userInfo) {
      fetchYearlySummary();
    }
  }, [userInfo]);

  // 년도 변경 시 월별 요약 로드
  useEffect(() => {
    if (userInfo) {
      fetchMonthlySummary(selectedYear);
    }
  }, [selectedYear, userInfo]);

  // 년/월 변경 시 상세 통계 로드
  useEffect(() => {
    if (userInfo) {
      fetchStatistics(selectedYear, selectedMonth);
    }
  }, [selectedYear, selectedMonth, userInfo]);

  // ScrollPicker용 연도 아이템
  const yearItems = useMemo(() => {
    const years = yearlyData.map(d => d.year).sort((a, b) => a - b);
    if (years.length === 0) {
      // 기본 연도 범위
      return [selectedYear - 1, selectedYear, selectedYear + 1].map(year => ({
        value: year,
        label: year.toString(),
        subLabel: '-'
      }));
    }
    const yearsWithSelection = Array.from(new Set([...years, selectedYear])).sort((a, b) => a - b);
    return yearsWithSelection.map(year => {
      const data = yearlyData.find(d => d.year === year);
      return {
        value: year,
        label: year.toString(),
        subLabel: formatRevenue(data?.total_revenue || 0)
      };
    });
  }, [selectedYear, yearlyData]);

  // ScrollPicker용 월 아이템
  const monthItems = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const data = monthlyData.find(d => d.month === month);
      return {
        value: month,
        label: month.toString(),
        subLabel: formatRevenue(data?.total_revenue || 0)
      };
    });
  }, [monthlyData]);

  // 비교 계산 (전월 대비)
  const getComparison = (field) => {
    if (!statistics?.statistics || !monthlyData.length) return null;

    const prevMonth = selectedMonth - 1;
    if (prevMonth < 1) return null;

    const prevData = monthlyData.find(d => d.month === prevMonth);
    if (!prevData) return null;

    const currentValue = field === 'revenue'
      ? statistics.statistics.total_estimated_revenue
      : statistics.statistics.total_reservations;
    const prevValue = field === 'revenue' ? prevData.total_revenue : prevData.total_reservations;

    if (!prevValue || prevValue === 0) return null;
    return ((currentValue - prevValue) / prevValue) * 100;
  };

  // 숫자 포맷팅
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat(TEXT.dateFormat[lang]).format(num);
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '€0';
    return `€${formatNumber(price)}`;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0%';
    return `${value.toFixed(1)}%`;
  };

  // 월별 추이 차트 데이터
  const monthlyTrendData = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const data = monthlyData.find(d => d.month === month);
      return {
        label: month.toString(),
        value: data?.total_revenue || 0
      };
    });
  }, [monthlyData]);

  if (loading && !statistics) {
    return <div className={styles.loading}>{TEXT.loading[lang]}</div>;
  }

  if (error) {
    return <div className={styles.error}>{TEXT.error[lang]}: {error}</div>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{TEXT.title[lang]}</h1>

      {/* 년/월 스크롤 피커 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
        <ScrollPicker
          items={yearItems}
          selectedValue={selectedYear}
          onChange={setSelectedYear}
        />
        <ScrollPicker
          items={monthItems}
          selectedValue={selectedMonth}
          onChange={setSelectedMonth}
          isMonth={true}
        />
      </div>

      {statistics && (
        <>
          {(statistics.statistics?.missing_exchange_rates || 0) > 0 && <p role="alert">{missingReservationRateText[lang] || missingReservationRateText.en}</p>}
          {/* Summary Cards */}
          <div className={styles.summaryGrid}>
            <div className={`${styles.card} ${styles.summaryItem}`}>
              <div className={styles.summaryLabel}>{TEXT.summary.totalReservations[lang]}</div>
              <div className={styles.summaryValue}>
                {formatNumber(statistics.statistics?.total_reservations || 0)}
                <ComparisonArrow value={getComparison('reservations')} />
              </div>
            </div>

            <div className={`${styles.card} ${styles.summaryItem}`}>
              <div className={styles.summaryLabel}>{TEXT.summary.totalRevenue[lang]}</div>
              <div className={styles.summaryValue}>
                {formatPrice(statistics.statistics?.total_estimated_revenue || 0)}
                <ComparisonArrow value={getComparison('revenue')} />
              </div>
            </div>

            <div className={`${styles.card} ${styles.summaryItem}`}>
              <div className={styles.summaryLabel}>{TEXT.summary.depositRate[lang]}</div>
              <div className={styles.summaryValue}>
                {formatPercentage(statistics.statistics?.deposit_received_rate || 0)}
              </div>
            </div>

            <div className={`${styles.card} ${styles.summaryItem}`}>
              <div className={styles.summaryLabel}>{TEXT.summary.avgPrice[lang]}</div>
              <div className={styles.summaryValue}>
                {formatPrice(statistics.statistics?.avg_estimated_price || 0)}
              </div>
            </div>
          </div>

          {/* Monthly Trend Chart */}
          <div className={styles.card} style={{ marginBottom: '8px' }}>
            <h3 className={styles.chartTitle}>{TEXT.sections.monthlyTrend[lang]} ({selectedYear})</h3>
            <BarChart
              data={monthlyTrendData}
              height={80}
              color="#999"
              textColor="#888"
              highlightIndex={selectedMonth - 1}
            />
          </div>

          {/* Charts Grid */}
          <div className={styles.chartsGrid}>
            {/* Reservation Types */}
            {statistics.statistics?.reservation_types && (
              <div className={styles.card}>
                <h3 className={styles.chartTitle}>{TEXT.sections.reservationTypes[lang]}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <PieChart
                    data={Object.entries(statistics.statistics.reservation_types).map(([type, data], index) => ({
                      value: data.count,
                      color: ['#333', '#666', '#999', '#bbb'][index] || '#eee'
                    }))}
                    size={45}
                  />
                  <div style={{ flex: 1 }}>
                    {Object.entries(statistics.statistics.reservation_types).map(([type, data], index) => (
                      <div key={type} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '2px',
                        fontSize: '9px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <div style={{
                            width: '5px',
                            height: '5px',
                            backgroundColor: ['#333', '#666', '#999', '#bbb'][index] || '#eee',
                            borderRadius: '50%',
                          }} />
                          <span style={{ color: '#666' }}>{TEXT.types[type]?.[lang] || type}</span>
                        </div>
                        <span style={{ fontWeight: '500', color: '#333' }}>{formatNumber(data.count)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Price Ranges */}
            {statistics.statistics?.price_ranges && Object.keys(statistics.statistics.price_ranges).length > 0 && (
              <div className={styles.card}>
                <h3 className={styles.chartTitle}>{TEXT.sections.priceRanges[lang]}</h3>
                <BarChart
                  data={Object.entries(statistics.statistics.price_ranges).map(([range, data]) => ({
                    label: TEXT.priceRanges[range]?.[lang] || range,
                    value: typeof data === 'object' ? data.count : data
                  }))}
                  height={70}
                  color="#999"
                  textColor="#666"
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ArtistReservationManager;
